<template>
  <ElAutocomplete
    v-slot="{ item }"
    :fetch-suggestions="querySearch"
    :trigger-on-focus="false"
    :debounce="250"
    :hide-loading="true"
    class="w-full"
    v-bind="$attrs"
    v-model="fieldValue"
    v-on="inputListeners"
    @select="handleSelect"
  >
    <FontAwesomeIcon
      :icon="['fas', 'map-marker-alt']"
      class="text-primary-light mr-15"
    />
    <span class="main-value text-primary-light">
      <span class="main-text">{{ mainTextLabel(item.structuredFormatting).firstPart }}</span>
      <span class="main-text font-bold">{{ mainTextLabel(item.structuredFormatting).matchingSubString }}</span>
      <span class="main-text">{{ mainTextLabel(item.structuredFormatting).lastPart }}</span>
    </span>
    <span class="text-xs text-gray-400 pl-5">{{ item.structuredFormatting.secondary_text }}</span>
  </ElAutocomplete>
</template>

<script>
import loadGoogleMapsPlaces from '@/utils/loadGoogleMapsPlaces';

const googleMapsPlacesAPIKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;

export default {
  props: {
    value: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      fieldValue: this.value,
      placesLibrary: null,
      // google.maps.places.AutocompleteService is closed to new customers since March 2025;
      // the new AutocompleteSuggestion needs Places API (New), so fall back if it is not enabled
      useLegacyAutocomplete: false,
      legacyAutocompleteService: null,
      sessionToken: null,
    };
  },
  mounted() {
    loadGoogleMapsPlaces(googleMapsPlacesAPIKey)
      .then((placesLibrary) => {
        this.placesLibrary = placesLibrary;

        if (!placesLibrary.AutocompleteSuggestion) this.enableLegacyAutocomplete();
        else this.sessionToken = new placesLibrary.AutocompleteSessionToken();
      })
      .catch((error) => {
        console.error(error);
      });
  },
  computed: {
    inputListeners() {
      const vm = this;
      // `Object.assign` merges objects together to form a new object
      // eslint-disable-next-line prefer-object-spread
      return Object.assign(
        {},
        // We add allEvents the listeners from the parent
        this.$listeners,
        // Then we can add custom listeners or override the
        // behavior of some listeners.
        {
          // This ensures that the component works with v-model
          input(newValue) {
            vm.$emit('input', newValue);
          },
          change(event) {
            vm.$emit('change', event);
          },
        },
      );
    },
  },
  methods: {
    querySearch(queryString, callback) {
      if (!this.placesLibrary || !queryString) {
        callback([]);

        return;
      }

      if (this.useLegacyAutocomplete) {
        this.legacyQuerySearch(queryString, callback);

        return;
      }

      this.placesLibrary.AutocompleteSuggestion
        .fetchAutocompleteSuggestions({ input: queryString, sessionToken: this.sessionToken })
        .then(({ suggestions }) => {
          callback(suggestions.map(this.mapSuggestion).filter((result) => result !== null));
        })
        .catch((error) => {
          // Places API (New) may be disabled for this key; keep the field working
          console.error(error);
          this.enableLegacyAutocomplete();
          this.legacyQuerySearch(queryString, callback);
        });
    },
    mapSuggestion(suggestion) {
      const prediction = suggestion.placePrediction;
      if (!prediction) return null;

      const { mainText } = prediction;
      const matches = (mainText && mainText.matches) || [];

      return {
        value: prediction.text ? prediction.text.toString() : '',
        label: prediction.placeId,
        placeID: prediction.placeId,
        structuredFormatting: {
          main_text: mainText ? mainText.toString() : '',
          secondary_text: prediction.secondaryText ? prediction.secondaryText.toString() : '',
          main_text_matched_substrings: matches.map((match) => ({
            offset: match.startOffset,
            length: match.endOffset - match.startOffset,
          })),
        },
      };
    },
    enableLegacyAutocomplete() {
      this.useLegacyAutocomplete = true;

      if (!this.legacyAutocompleteService && this.placesLibrary.AutocompleteService) {
        this.legacyAutocompleteService = new this.placesLibrary.AutocompleteService();
      }
    },
    legacyQuerySearch(queryString, callback) {
      if (!this.legacyAutocompleteService) {
        callback([]);

        return;
      }

      this.legacyAutocompleteService.getQueryPredictions({ input: queryString }, (predictions, status) => {
        const { google } = window;
        if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
          callback([]);

          return;
        }

        callback(predictions.map((prediction) => ({
          value: prediction.description,
          label: prediction.place_id,
          structuredFormatting: prediction.structured_formatting,
          placeID: prediction.place_id,
        })));
      });
    },
    mainTextLabel(structuredFormatting) {
      const matchedSubStrings = structuredFormatting.main_text_matched_substrings || [];
      const matchedSubString = matchedSubStrings[0];
      // Google does not always return a matched substring (Sentry EVENTOR-VUEJS-HP)
      if (!matchedSubString) {
        return { firstPart: structuredFormatting.main_text || '', matchingSubString: '', lastPart: '' };
      }

      const matchedSubStringOffset = matchedSubString.offset;
      const matchedSubStringLength = matchedSubString.length;
      const firstPart = structuredFormatting.main_text.substring(0, matchedSubStringOffset);
      const matchingSubString = structuredFormatting.main_text.substring(
        matchedSubStringOffset,
        matchedSubStringLength + matchedSubStringOffset,
      );
      const lastPart = structuredFormatting.main_text.substring(
        matchedSubStringLength + matchedSubStringOffset,
        structuredFormatting.main_text.length,
      );

      return {
        firstPart,
        matchingSubString,
        lastPart,
      };
    },
    handleSelect(item) {
      this.fieldValue = item.value;
      this.$emit('google-place-id-update', item.placeID);

      // A session token covers one autocomplete session, from typing to selection
      if (this.placesLibrary && this.placesLibrary.AutocompleteSessionToken) {
        this.sessionToken = new this.placesLibrary.AutocompleteSessionToken();
      }
    },
  },
};
</script>

<style scoped>
li {
  line-height: normal;
  padding: 7px;

  .main-value {
    @apply overflow-hidden;
    text-overflow: ellipsis;

    .main-text {
      @apply inline-block;

      white-space: pre-wrap;
    }
  }

  &:hover .icon, &.highlighted .icon {
    @apply text-secondary;
  }
}
</style>
